import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppSettings } from 'src/app/app.settings';
import { Settings } from '../../../app.settings.model';
import { MessageDialogComponent } from '../../../shared-component/message-dialog/message-dialog.component';
import { SetupsService } from '../setup.service';

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  // ============================================================
  // SETTINGS / UI STATE
  // ============================================================
  settings: Settings;

  /**
   * UI flag used by:
   * - button disabled state
   * - showing mat-spinner
   */
  loadingIndicator = false;
  oldPasswordHidden = true;
  passwordHidden = true;
  confirmPasswordHidden = true;

  // ============================================================
  // INPUT DATA
  // ============================================================
  /**
   * Provided from the caller via MAT_DIALOG_DATA
   * Expected shape: { staffObj: ... }
   */
  staffObj: any;

  // ============================================================
  // FORM
  // ============================================================
  /**
   * Reactive form backing the dialog inputs.
   * Validators: all required (kept as-is).
   */
  changePasswordForm: UntypedFormGroup;

  // RxJS teardown
  private destroy$ = new Subject<void>();

  // ============================================================
  // DI / CONSTRUCTOR
  // ============================================================
  constructor(
    public dialogRef: MatDialogRef<ChangePasswordComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,

    private formBuilder: UntypedFormBuilder,

    public appSettings: AppSettings,
    public messageDialog: MatDialog,
    private translate: TranslateService,
    public setupsService: SetupsService,

    private cd: ChangeDetectorRef
  ) {
    // app-level settings used in template ([dir] RTL/LTR)
    this.settings = this.appSettings.settings;
  }

  // ============================================================
  // LIFECYCLE: INIT
  // ============================================================
  ngOnInit(): void {
    // Read incoming staff object (if provided)
    this.staffObj = this.data?.staffObj;

    /**
     * Default username:
     * - prefer staff email
     * - fallback to staff userName
     * - otherwise empty string
     */
    const userName = this.staffObj?.email ?? this.staffObj?.userName ?? '';

    // Build the form (all controls required)
    this.changePasswordForm = this.formBuilder.group({
      userName: [userName, Validators.required],
      oldPassword: ['', Validators.required],
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required],
    }, { validators: this.passwordsMatchValidator });

    // OnPush: force view update after building form
    this.cd.markForCheck();
  }

  // ============================================================
  // MAIN ACTION: CHANGE PASSWORD
  // ============================================================
  /**
   * Flow:
   * 1) Guard form validity
   * 2) Validate password match client-side
   * 3) Set loadingIndicator + call API
   * 4) On success:
   *    - show success message
   *    - close dialog(true)
   * 5) On error:
   *    - close dialog(false)
   */
  changePassword(): void {
    this.changePasswordForm.markAllAsTouched();
    this.changePasswordForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    this.cd.markForCheck();

    if (!this.changePasswordForm.valid) {
      if (this.changePasswordForm.hasError('passwordMismatch')) {
        this.messageDialog.open(MessageDialogComponent, {
          data: { message: this.translate.instant('Passwords do not match') }
        });
      }
      return;
    }

    // Start loading
    this.loadingIndicator = true;
    this.cd.markForCheck();

    // API call
    this.setupsService.changePassword(this.changePasswordForm.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadingIndicator = false;

          this.messageDialog.open(MessageDialogComponent, {
            data: { message: this.translate.instant('Password updated successfully') }
          });

          // Tell caller success
          this.dialogRef.close(true);

          this.cd.markForCheck();
        },
        error: () => {
          // Stop loading and tell caller failure
          this.loadingIndicator = false;
          this.dialogRef.close(false);
          this.cd.markForCheck();
        }
      });
  }

  // ============================================================
  // CANCEL / CLOSE
  // ============================================================
  onNoClick(): void {
    // Caller interprets false as cancelled/failed
    this.dialogRef.close(false);
  }

  get passwordMismatch(): boolean {
    const cp = this.changePasswordForm?.get('confirmPassword');
    return !!(cp?.hasError('passwordMismatch') && (cp?.touched || cp?.dirty));
  }

  onConfirmPasswordBlur(): void {
    const cp = this.changePasswordForm?.get('confirmPassword');
    cp?.markAsTouched();
    cp?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    this.changePasswordForm?.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    // OnPush: ensure UI updates immediately on blur
    this.cd.detectChanges();
  }

  private passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value ?? '';
    const confirmControl = control.get('confirmPassword');
    const confirmPassword = confirmControl?.value ?? '';
    if (!confirmControl) return null;

    const currentErrors = confirmControl.errors || {};
    if (password !== confirmPassword) {
      if (!currentErrors['passwordMismatch']) {
        confirmControl.setErrors({ ...currentErrors, passwordMismatch: true });
      }
      return { passwordMismatch: true };
    }

    if (currentErrors['passwordMismatch']) {
      const { passwordMismatch, ...rest } = currentErrors;
      confirmControl.setErrors(Object.keys(rest).length ? rest : null);
    }
    return null;
  }

  // ============================================================
  // LIFECYCLE: DESTROY
  // ============================================================
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}